import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  ChevronRight,
  Copy,
  Loader2,
  MessageSquareWarning,
  Route,
  X,
} from 'lucide-react';
import { softenArkoVisibleCopy } from './arkoVisibleLanguage';
import {
  buildReasoningNarrative,
  buildReasoningTraceSupportCopy,
  hasReasoningTraceData,
  reconcileExecutedRoute,
  type ReasoningNarrativeFacts,
  type ReasoningTrace,
} from './reasoningTrace';
import {
  isPortalFlywheelRoutingEnabled,
  reportWrongRoutingPath,
} from './portalFlywheelRouting';

interface ArkoReasoningTraceCardProps {
  trace: ReasoningTrace | null | undefined;
  /** Badge und Nachweise desselben Laufs. Ohne Facts liest die Spur nur die Pipeline. */
  facts?: ReasoningNarrativeFacts;
  onFeedbackConfirmed?: (message: string) => void;
  /** In der Aktionszeile neben den Anschluss-Chips, nicht als eigener Block. */
  rowFit?: boolean;
}

export const ArkoReasoningTraceCard: React.FC<ArkoReasoningTraceCardProps> = ({
  trace,
  facts,
  onFeedbackConfirmed,
  rowFit = false,
}) => {
  const feedbackCommentId = useId();
  const feedbackTitleId = useId();
  const feedbackTextareaRef = useRef<HTMLTextAreaElement>(null);
  const [copied, setCopied] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackComment, setFeedbackComment] = useState('');
  const [feedbackError, setFeedbackError] = useState('');
  const [feedbackPending, setFeedbackPending] = useState(false);
  const [feedbackReported, setFeedbackReported] = useState(false);

  const closeFeedbackModal = useCallback(() => {
    if (feedbackPending) return;
    setFeedbackOpen(false);
    setFeedbackError('');
  }, [feedbackPending]);

  useEffect(() => {
    if (!feedbackOpen || feedbackReported) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeFeedbackModal();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [closeFeedbackModal, feedbackOpen, feedbackReported]);

  useEffect(() => {
    if (!feedbackOpen || feedbackReported) return;
    feedbackTextareaRef.current?.focus();
  }, [feedbackOpen, feedbackReported]);

  const handleCopy = useCallback(async () => {
    if (!trace) return;
    const text = buildReasoningTraceSupportCopy(trace, facts);
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      /* Clipboard nicht verfügbar */
    }
  }, [facts, trace]);

  const handleWrongPath = useCallback(async () => {
    if (!trace || feedbackPending || feedbackReported) return;
    setFeedbackPending(true);
    setFeedbackError('');
    try {
      const confirmation = await reportWrongRoutingPath(trace, feedbackComment);
      setFeedbackReported(true);
      setFeedbackOpen(false);
      onFeedbackConfirmed?.(confirmation);
    } catch (error) {
      setFeedbackError(error instanceof Error ? error.message : 'Feedback konnte nicht gespeichert werden.');
    } finally {
      setFeedbackPending(false);
    }
  }, [feedbackComment, feedbackPending, feedbackReported, onFeedbackConfirmed, trace]);

  if (!hasReasoningTraceData(trace) || !trace) return null;

  const shown = reconcileExecutedRoute(trace);

  const canCopy = Boolean(trace.request_id || trace.execution_id || (trace.pipeline ?? []).length > 0);
  const canReportWrongPath =
    isPortalFlywheelRoutingEnabled() && Boolean(trace.request_id || trace.execution_id);

  const feedbackModal =
    feedbackOpen && !feedbackReported && typeof document !== 'undefined'
      ? createPortal(
          <div
            className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 p-4 backdrop-blur-[2px]"
            role="dialog"
            aria-modal="true"
            aria-labelledby={feedbackTitleId}
            onClick={(event) => {
              if (event.target === event.currentTarget) closeFeedbackModal();
            }}
          >
            <div className="w-full max-w-md rounded-2xl border border-white/[0.08] bg-slate-950 shadow-2xl">
              <div className="flex items-center gap-2 border-b border-white/[0.06] px-4 py-3">
                <MessageSquareWarning className="h-4 w-4 text-amber-300/80" aria-hidden />
                <h3 id={feedbackTitleId} className="flex-1 text-sm font-semibold text-white">
                  Falscher Pfad melden
                </h3>
                <button
                  type="button"
                  onClick={closeFeedbackModal}
                  disabled={feedbackPending}
                  className="rounded p-1 text-slate-500 transition-colors hover:bg-white/[0.06] hover:text-slate-300 disabled:cursor-not-allowed disabled:opacity-50"
                  aria-label="Dialog schließen"
                >
                  <X className="h-4 w-4" aria-hidden />
                </button>
              </div>
              <div className="space-y-3 px-4 py-4">
                <p className="text-[11px] leading-relaxed text-slate-400">
                  Nur Hinweise zum Antwortweg — keine Dokumentinhalte oder Mandantennamen eingeben.
                </p>
                <label className="block text-[10px] text-slate-500" htmlFor={feedbackCommentId}>
                  Optionaler Hinweis
                </label>
                <textarea
                  ref={feedbackTextareaRef}
                  id={feedbackCommentId}
                  value={feedbackComment}
                  maxLength={1000}
                  rows={3}
                  onChange={(event) => setFeedbackComment(event.target.value)}
                  placeholder="Was wäre der richtige Pfad gewesen?"
                  className="w-full resize-y rounded-xl border border-white/[0.08] bg-black/30 px-3 py-2 text-sm text-slate-200 outline-none placeholder:text-slate-600 focus:border-amber-400/30"
                />
                {feedbackError ? <p className="text-xs text-rose-300">{feedbackError}</p> : null}
                <div className="flex items-center justify-end gap-2">
                  <button
                    type="button"
                    disabled={feedbackPending}
                    onClick={closeFeedbackModal}
                    className="rounded-lg px-3 py-1.5 text-xs text-slate-400 transition-colors hover:bg-white/[0.04] hover:text-slate-200 disabled:opacity-50"
                  >
                    Abbrechen
                  </button>
                  <button
                    type="button"
                    disabled={feedbackPending}
                    onClick={() => void handleWrongPath()}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-amber-400/25 bg-amber-400/[0.08] px-3 py-1.5 text-xs font-medium text-amber-100 transition-colors hover:bg-amber-400/[0.14] disabled:opacity-50"
                  >
                    {feedbackPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> : null}
                    Meldung senden
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body,
        )
      : null;

  return (
    <>
      <details className={`${rowFit ? 'w-fit max-w-full' : 'mt-1.5 max-w-[80%]'} group`}>
        <summary className="list-none cursor-pointer select-none flex items-center gap-1">
          <ChevronRight
            className="w-3 h-3 text-slate-500 transition-transform group-open:rotate-90"
            aria-hidden
          />
          <span className="inline-flex items-center gap-1 rounded-full border border-white/[0.08] bg-white/[0.03] px-2 py-0.5 text-[10px] font-semibold text-slate-400 hover:text-slate-200 hover:border-violet-500/30 transition-colors">
            <Route className="w-3 h-3" aria-hidden />
            <span>So bin ich vorgegangen</span>
          </span>
        </summary>
        <div className="mt-2 pl-1 space-y-2 border-l border-white/[0.08] ml-1">
          <ul className="space-y-1 text-[11px] leading-relaxed text-slate-300">
            {buildReasoningNarrative(shown, facts).map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
          {trace.error ? (
            <p className="text-[11px] leading-relaxed text-rose-300">
              {softenArkoVisibleCopy(trace.error)}
            </p>
          ) : null}
          <div className="flex flex-wrap items-center gap-1.5">
            {canCopy ? (
              <button
                type="button"
                onClick={() => void handleCopy()}
                className="inline-flex items-center gap-1 rounded border border-white/[0.08] bg-white/[0.03] px-2 py-1 text-[10px] text-slate-400 hover:text-slate-200 hover:border-violet-500/30 transition-colors"
              >
                <Copy className="w-3 h-3" aria-hidden />
                {copied ? 'Kopiert' : 'Support-Diagnose kopieren'}
              </button>
            ) : null}
            {canReportWrongPath ? (
              <button
                type="button"
                disabled={feedbackPending || feedbackReported}
                onClick={() => {
                  setFeedbackError('');
                  setFeedbackOpen(true);
                }}
                className="inline-flex items-center gap-1 rounded px-1.5 py-1 text-[9px] text-slate-600 hover:text-amber-300/80 transition-colors disabled:cursor-default disabled:text-emerald-500/70"
              >
                <MessageSquareWarning className="w-3 h-3" aria-hidden />
                {feedbackReported ? 'Pfad gemeldet' : 'Falscher Pfad melden'}
              </button>
            ) : null}
          </div>
        </div>
      </details>
      {feedbackModal}
    </>
  );
};
